import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useDataManipulation } from "../hooks/usedatamanipulation";
import {
  StateDropdown,
  LicenseDropdown,
  CredentialingDropdown,
  TimezoneDropdown,
  CoursesDropdown,
} from "./dropdowns";
import { CoursesMinEditor, AvailabilityEditor } from "./editors";
import { formatDateForStorage, getMonthsFromDates, parseDateOnlyValue } from "./utils";

export const getTimezoneAbbr = (timezone) => {
  if (!timezone) return "";
  const now = new Date();
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    timeZoneName: "short",
  });
  const parts = formatter.formatToParts(now);
  const tzPart = parts.find((part) => part.type === "timeZoneName");
  return tzPart ? tzPart.value : "";
};

function JsonRenderer({ data, onDataChange, changedFields = new Set(), setNotification, currentBackupVersion, isLoadedFromBackup, coursesOptions: propCoursesOptions }) {
  // Mapping for user-friendly field names in general section
  const fieldLabelMap = {
    startDate: "Start Date",
    endDate: "End Date",
    maxPatientsPerAppt: "Maximum Patient per Appointment",
    absenteeRate: "Absentee Rate",
  };

  // Mapping for user-friendly field names in practice section
  const practiceFieldLabelMap = {
    workingHours: "Working Hours",
    totalPatients: "Total Patients",
    name: "Name",
    state: "State",
    timezone: "Timezone",
    //priority: "Priority",
  };

  // Mapping for user-friendly field names in provider section
  const providerFieldLabelMap = {
    name: "Name",
  };

  // State for courses options
  const [coursesOptions, setCoursesOptions] = useState([]);

  // Update local coursesOptions when prop changes
  useEffect(() => {
    if (propCoursesOptions) {
      setCoursesOptions(propCoursesOptions);
    }
  }, [propCoursesOptions]);

  const [collapsedSections, setCollapsedSections] = useState(() => {
    const initial = {};
    ["general", "appointmentSlots"].forEach(
      (key) => (initial[key] = true)
    );
    initial["practices"] =  true;
    initial["providers"] =  true;
    (data.practices || []).forEach((_, index) => {
      initial[`practice-${index}`] =  true;
    });
    (data.providers || []).forEach(
      (_, index) => (initial[`provider-${index}`] =  true)
    );
    return initial;
  });
  const [editingStates, setEditingStates] = useState({});

  const { updateDataAtPath, addItemAtPath, removeItemAtPath } =
    useDataManipulation(data, onDataChange);

  // Keep coursesMin arrays aligned with the active month span derived from start/end dates.
  const alignCoursesMinToMonths = (draftData, startDate, endDate) => {
    if (!startDate || !endDate) return draftData;
    const months = getMonthsFromDates(startDate, endDate);
    if (!months.length) return draftData;

    const updatedPractices = (draftData.practices || []).map((practice) => {
      const existing = Array.isArray(practice.coursesMin)
        ? practice.coursesMin.slice(0, months.length).map((entry) => entry || {})
        : [];

      while (existing.length < months.length) {
        existing.push({});
      }

      return { ...practice, coursesMin: existing };
    });

    return { ...draftData, practices: updatedPractices };
  };

  const toggleSection = (section) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const renderPrimitiveField = (key, value, path) => {
    // Determine if the field is inside a practice or provider
    const isInPractice = path.length > 0 && path[0] === "practices";
    const isInProvider = path.length > 0 && path[0] === "providers";

    // Fields that are exempt from mandatory requirement
    const exemptFields = isInProvider ? ["availability"] : isInPractice ? ["priority"] : [];

    // Check if the field is inside inclusions (which should not be mandatory)
    const isInInclusions = path.some((p) => p === "inclusions");

    // Check if the field is mandatory (inside practice or provider and not exempt and not in inclusions)
    const isMandatory =
      (isInPractice || isInProvider) &&
      !exemptFields.includes(key) &&
      !isInInclusions;

    // For INCLUDE_EXISTING_APPOINTMENTS key, render as toggle checkbox
    if (key === "INCLUDE_EXISTING_APPOINTMENTS") {
      const isChecked = value === "true";
      const displayKey = "Include Existing Appointments";
      return (
        <div key={key} className="object-field primitive-field">
          <label className="field-name">
            {displayKey}:
          </label>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={isChecked}
              onChange={(e) => {
                const newValue = e.target.checked ? "true" : "false";
                onDataChange(updateDataAtPath(path, newValue));
              }}
              className={`primitive-field-input ${changedFields.has(path.join(".")) ? "changed-field" : ""}`}
            />
            <span className="slider"></span>
          </label>
        </div>
      );
    }

    // For startDate and endDate keys, render DatePicker with MM-DD-YYYY format
    const lowerKey = key.toLowerCase();
    const displayKey =
      (isInPractice ? practiceFieldLabelMap[key] : null) ||
      (isInProvider ? providerFieldLabelMap[key] : null) ||
      fieldLabelMap[key] ||
      key;
    if (lowerKey === "startdate" || lowerKey === "enddate") {
      // Parse date-only values as local dates so the same calendar day is shown everywhere.
      let selectedDate = null;
      if (value) {
        const parsedDate = parseDateOnlyValue(value);
        if (parsedDate) {
          selectedDate = parsedDate;
        }
      }
      return (
        <div key={key} className="object-field primitive-field">
          <label className="field-name">
            {displayKey}
            {isMandatory ? <span className="mandatory-indicator">*</span> : ""}:
          </label>
          <DatePicker
            selected={selectedDate}
              onChange={(date) => {
                if (date) {
                  const dateStr = formatDateForStorage(date);
                  const updated = alignCoursesMinToMonths(
                    updateDataAtPath(path, dateStr),
                    lowerKey === "startdate" ? dateStr : data.startDate,
                    lowerKey === "enddate" ? dateStr : data.endDate
                  );
                  onDataChange(updated);
                } else {
                  onDataChange(updateDataAtPath(path, ""));
                }
              }}
            dateFormat="MM-dd-yyyy"
            placeholderText="MM-DD-YYYY"
            className={`primitive-field-input ${
              changedFields.has(path.join(".")) ? "changed-field" : ""
            }`}
            minDate={new Date()} // Prevent selecting past dates
          />
        </div>
      );
    }
    if (lowerKey === "absenteerate") {
      const inputType = "number";
      return (
        <div key={key} className="object-field primitive-field">
          <label className="field-name">
            {displayKey}
            {isMandatory ? <span className="mandatory-indicator">*</span> : ""}:
          </label>
          <input
            type={inputType}
            value={
              value === undefined || value === null || value === 0
                ? ""
                : value * 100
            }
            min={0}
            max={100}
            step={0.01}
            title="Enter the absentee percentage amount( 0 to 100 )"
            onChange={(e) => {
              let newValue =
                e.target.value === "" ? "" : Number(e.target.value);
              if (newValue !== "" && (newValue < 0 || newValue > 100)) {
                // Ignore invalid values outside 0-100 range
                return;
              }
              // Store as decimal percentage (divide by 100)
              newValue = newValue === "" ? "" : newValue / 100;
              onDataChange(updateDataAtPath(path, newValue));
            }}
            className={`primitive-field-input tooltip ${
              changedFields.has(path.join(".")) ? "changed-field" : ""
            }`}
          />
          <span className="tooltiptext"></span>
        </div>
      );
    }
    const inputType = typeof value === "number" ? "number" : "text";
    const isMaxPatients = lowerKey === "maxpatientsperappt";
    return (
      <div key={key} className="object-field primitive-field">
        <label className="field-name">
          {displayKey}
          {isMandatory ? <span className="mandatory-indicator">*</span> : ""}:
        </label>
        <input
          type={inputType}
          value={value}
          min={isMaxPatients ? 0 : undefined}
          max={isMaxPatients ? 1000 : undefined}
          step={isMaxPatients ? 1 : undefined}
          onChange={(e) => {
            let newValue = e.target.value;
            if (inputType === "number") {
              newValue = e.target.value === "" ? "" : Number(e.target.value);
              if (
                isMaxPatients &&
                (newValue < 0 || newValue > 1000 || !Number.isInteger(newValue))
              ) {
                return; // Ignore invalid values
              }
            }
            onDataChange(updateDataAtPath(path, newValue));
          }}
          className={`primitive-field-input ${
            changedFields.has(path.join(".")) ? "changed-field" : ""
          }`}
        />
      </div>
    );
  };

  // Utility function to format date from YYYY-MM-DD to MM-DD-YYYY
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      return `${parts[1]}-${parts[2]}-${parts[0]}`;
    }
    return dateStr;
  };

  // Utility function for custom JSON stringify that formats dates
  const stringifyWithDateFormat = (obj) => {
    return JSON.stringify(
      obj,
      (key, value) => {
        if (
          key === "date" &&
          typeof value === "string" &&
          value.includes("-")
        ) {
          return formatDateDisplay(value);
        }
        return value;
      },
      2
    );
  };

  // Generic renderField function to handle primitives, objects, and arrays recursively
  const renderField = (key, value, path) => {
    if (Array.isArray(value)) {
      return (
        <div key={key} className="array-field">
          <h5>{key}</h5>
          <ul>
            {value.map((item, index) => (
              <li key={index} className="array-item">
                {typeof item === "object" && item !== null ? (
                  <div className="nested-object">
                    <pre>{stringifyWithDateFormat(item)}</pre>
                  </div>
                ) : (
                  <input
                    type="text"
                    value={item}
                    onChange={(e) =>
                      onDataChange(
                        updateDataAtPath([...path, index], e.target.value)
                      )
                    }
                  />
                )}
                <button
                  type="button"
                  className="remove-btn"
                  onClick={() =>
                    onDataChange(removeItemAtPath([...path, index]))
                  }
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="add-btn"
            onClick={() => onDataChange(addItemAtPath(path))}
          >
            Add Item
          </button>
        </div>
      );
    } else if (typeof value === "object" && value !== null) {
      return (
        <div key={key} className="nested-object">
          <h5>{key}</h5>
          {Object.entries(value).map(([subKey, subValue]) =>
            renderField(subKey, subValue, [...path, subKey])
          )}
        </div>
      );
    } else {
      return renderPrimitiveField(key, value, path);
    }
  };

  // WeeklySlotsEditor component for adding/removing weekly slots with dropdown
  const WeeklySlotsEditor = ({ weeklySlots, onChange, path }) => {
    const [selectedDay, setSelectedDay] = React.useState("");
    const allDays = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];
    const availableDays = allDays.filter((day) => !weeklySlots.includes(day));

    const dayOrder = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];

    const sortDays = (days) => {
      return days.sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b));
    };

    const handleAddDay = () => {
      if (!selectedDay) return;
      const newWeeklySlots = sortDays([...weeklySlots, selectedDay]);
      onChange(updateDataAtPath(path, newWeeklySlots));
      setSelectedDay("");
    };

    const handleRemoveDay = (day) => {
      const newWeeklySlots = sortDays(weeklySlots.filter((d) => d !== day));
      onChange(updateDataAtPath(path, newWeeklySlots));
    };

    return (
      <div className="weekly-slots-editor">
        <h5>Weekly Slots</h5>
        <div className="flex-center">
          <select
            value={selectedDay}
            onChange={(e) => setSelectedDay(e.target.value)}
            className="day-select mr10"
          >

            <option value="">Select a day</option>
            {availableDays.map((day) => (
              <option key={day} value={day}>
                {day.charAt(0).toUpperCase() + day.slice(1)}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleAddDay}
            className="btn-green"
            disabled={!selectedDay}
          >
            Add
          </button>
        </div>
        <ul className="ul-none">
          {weeklySlots.map((day, index) => (
            <li key={index} className="weekly-slot-item">
              <span className="capitalize">{day}</span>
              <button
                type="button"
                onClick={() => handleRemoveDay(day)}
                className="btn-red"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  // Utility functions for 12-hour / 24-hour conversion
  const convert12To24 = (hour12, period) => {
    let hour24 = parseInt(hour12, 10);
    if (period === "AM") {
      if (hour24 === 12) hour24 = 0;
    } else { // PM
      if (hour24 !== 12) hour24 += 12;
    }
    return hour24.toString().padStart(2, "0");
  };

  const convert24To12 = (hour24) => {
    const hour = parseInt(hour24, 10);
    const period = hour >= 12 ? "PM" : "AM";
    let hour12 = hour % 12;
    if (hour12 === 0) hour12 = 12;
    return { hour12: hour12.toString(), period };
  };

  const formatTime12Hour = (time24) => {
    const [hour, minute] = time24.split(":");
    const { hour12, period } = convert24To12(hour);
    return `${hour12}:${minute} ${period}`;
  };

  const formatSlotTo12Hour = (slot) => {
    const [start, end] = slot.split("-");
    return `${formatTime12Hour(start)} - ${formatTime12Hour(end)}`;
  };

  // New component for Appointment Slots Editor to fix React hooks usage
  const AppointmentSlotsEditor = ({
    appointmentSlots,
    path,
    data,
    onDataChange,
  }) => {
    const [newStartHour, setNewStartHour] = React.useState("");
    const [newStartMinute, setNewStartMinute] = React.useState("");
    const [newStartPeriod, setNewStartPeriod] = React.useState("AM");
    const [newEndHour, setNewEndHour] = React.useState("");
    const [newEndMinute, setNewEndMinute] = React.useState("");
    const [newEndPeriod, setNewEndPeriod] = React.useState("AM");


    const sortSlotsByStartTime = (slots) => {
      return slots.sort((a, b) => {
        const getStartTime = (slot) => slot.split("-")[0];
        const timeA = getStartTime(a);
        const timeB = getStartTime(b);
        return timeA.localeCompare(timeB);
      });
    };

    const handleAddDailySlot = () => {
      if (
        newStartHour === "" ||
        newStartMinute === "" ||
        newEndHour === "" ||
        newEndMinute === ""
      )
        return;

      // Convert 12-hour to 24-hour format
      const startHour24 = convert12To24(newStartHour, newStartPeriod);
      const endHour24 = convert12To24(newEndHour, newEndPeriod);

      const formattedSlot = `${startHour24}:${newStartMinute}-${endHour24}:${newEndMinute}`;

      // Validation for overlapping slots
      const isOverlapping = (newSlot, existingSlots) => {
        const [newStart, newEnd] = newSlot.split("-").map((t) => t.trim());
        for (const slot of existingSlots) {
          const [start, end] = slot.split("-").map((t) => t.trim());
          if (
            (newStart >= start && newStart < end) ||
            (newEnd > start && newEnd <= end) ||
            (newStart <= start && newEnd >= end)
          ) {
            return true;
          }
        }
        return false;
      };

      if (isOverlapping(formattedSlot, appointmentSlots.daily || [])) {
        if (setNotification) {
          console.warn('Using setNotification for overlapping daily slot');
          setNotification({ type: 'error', message: 'The new slot overlaps with an existing slot. Please choose a different time.' });
        } else {
          console.warn('Using alert for overlapping daily slot');
          alert("The new slot overlaps with an existing slot. Please choose a different time.");
        }
        return;
      }

      const newDailySlots = sortSlotsByStartTime([
        ...(appointmentSlots.daily || []),
        formattedSlot,
      ]);
      onDataChange(updateDataAtPath([...path, "daily"], newDailySlots));
      setNewStartHour("");
      setNewStartMinute("");
      setNewStartPeriod("AM");
      setNewEndHour("");
      setNewEndMinute("");
      setNewEndPeriod("AM");
    };

    const handleRemoveDailySlot = (index) => {
      const newDailySlots = [...(appointmentSlots.daily || [])];
      newDailySlots.splice(index, 1);
      onDataChange(updateDataAtPath([...path, "daily"], newDailySlots));
    };

    const hourOptions12 = Array.from({ length: 12 }, (_, i) =>
      (i + 1).toString()
    );
    const minuteOptions = ["00", "15", "30", "45"];
    const periodOptions = ["AM", "PM"];

    return (
      <div className="appointment-slots-container">
        <div className="appointment-slot-box">
          <h5>Daily Slots</h5>
          <div className="slot-coverage-explanation">
            This represents the total slot coverage across all time zones.
          </div>
          <ul className="ul-none">
            {(appointmentSlots.daily || []).map((slot, index) => (
              <li key={index} className="daily-slot-item">
                <span>{formatSlotTo12Hour(slot)}</span>
                <button
                  type="button"
                  className="btn-red"
                  onClick={() => handleRemoveDailySlot(index)}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
          <div className="flex-center">
            <select
              value={newStartHour}
              onChange={(e) => setNewStartHour(e.target.value)}
              className="mr5"
              aria-label="Select start hour"
            >
              <option value="">HH</option>
              {hourOptions12.map((hour) => (
                <option key={hour} value={hour}>
                  {hour}
                </option>
              ))}
            </select>
            <select
              value={newStartMinute}
              onChange={(e) => setNewStartMinute(e.target.value)}
              className="mr10"
              aria-label="Select start minute"
            >
              <option value="">MM</option>
              {minuteOptions.map((min) => (
                <option key={min} value={min}>
                  {min}
                </option>
              ))}
            </select>
            <select
              value={newStartPeriod}
              onChange={(e) => setNewStartPeriod(e.target.value)}
              className="mr10"
              aria-label="Select start period"
            >
              {periodOptions.map((period) => (
                <option key={period} value={period}>
                  {period}
                </option>
              ))}
            </select>
            <span className="mr10">to</span>
            <select
              value={newEndHour}
              onChange={(e) => setNewEndHour(e.target.value)}
              className="mr5"
              aria-label="Select end hour"
            >
              <option value="">HH</option>
              {hourOptions12.map((hour) => (
                <option key={hour} value={hour}>
                  {hour}
                </option>
              ))}
            </select>
            <select
              value={newEndMinute}
              onChange={(e) => setNewEndMinute(e.target.value)}
              className="mr10"
              aria-label="Select end minute"
            >
              <option value="">MM</option>
              {minuteOptions.map((min) => (
                <option key={min} value={min}>
                  {min}
                </option>
              ))}
            </select>
            <select
              value={newEndPeriod}
              onChange={(e) => setNewEndPeriod(e.target.value)}
              className="mr10"
              aria-label="Select end period"
            >
              {periodOptions.map((period) => (
                <option key={period} value={period}>
                  {period}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn-green"
              onClick={handleAddDailySlot}
            >
              Add
            </button>
          </div>
        </div>
        <div className="weekDay-slot-box">
          <WeeklySlotsEditor
            weeklySlots={appointmentSlots.weekly || []}
            onChange={onDataChange}
            path={[...path, "weekly"]}
          />
        </div>
      </div>
    );
  };

  // Replace renderAppointmentSlots call with AppointmentSlotsEditor component usage

  const renderSection = (title, content, sectionKey) => {
    const isCollapsed = collapsedSections[sectionKey];
    return (
      <div className="section-box">
        <div
          className="section-header"
          onClick={() => toggleSection(sectionKey)}
        >
          <h3>{title}</h3>
          <span className="collapse-icon">{isCollapsed ? "+" : "-"}</span>
        </div>
        <div
          className={`section-content ${
            isCollapsed ? "collapsed" : "expanded"
          }`}
        >
          {content}
        </div>
      </div>
    );
  };

  const renderGeneralSection = () => {
    const generalFields = {};
    Object.entries(data).forEach(([key, value]) => {
      if (
        key !== "appointmentSlots" &&
        key !== "practices" &&
        key !== "providers"
      ) {
        generalFields[key] = value;
      }
    });
    // Ensure INCLUDE_EXISTING_APPOINTMENTS is always displayed
    if (!generalFields.hasOwnProperty("INCLUDE_EXISTING_APPOINTMENTS")) {
      generalFields.INCLUDE_EXISTING_APPOINTMENTS = "true";
    }
    
    // Hide USE_STATIC_COURSES field completely (do not render)
    delete generalFields.USE_STATIC_COURSES;
    return (
      <div className="general-section">
        {isLoadedFromBackup && currentBackupVersion && (
          <div key="backupVersion" className="object-field primitive-field">
            <label className="field-name">Backup Version:</label>
            <input
              type="text"
              value={currentBackupVersion}
              readOnly
              className="primitive-field-input"
            />
          </div>
        )}
        {Object.entries(generalFields).map(([key, value]) =>
          renderField(key, value, [key])
        )}
      </div>
    );
  };

  const renderPracticesSection = () => {
    const practices = data.practices || [];

    const renderPracticeItem = (practice, index) => {
      const path = ["practices", index];

      // Use a local variable to track if courses have been rendered for this practice
      let hasRenderedCourses = false;

      const renderField = (key, value) => {
        if (key === "coursesMin") {
          return (
            <CoursesMinEditor
              key={key}
              value={value}
              onChange={onDataChange}
              path={[...path, key]}
              data={data}
            />
          );
        }
        if (key === "courses") {
          // Render existing courses only in the box model with remove buttons
          // Remove any horizontal display of existing courses
          // Remove any duplicate rendering of existing courses
          // Ensure only one rendering of courses exists
          // Check if this is the first rendering of courses in this practice to avoid duplicates
          if (hasRenderedCourses) {
            return null; // Skip duplicate rendering
          }
          hasRenderedCourses = true;
          return (
            <div key={key} className="object-field primitive-field">
              
                <label className="field-name">
                  Courses:
                </label>
                <div className="courses-container">
                <div className="course-items">
                  {value.map((course, idx) => {
                    const arrayPath = [...path, key];
                    return (
                      <div
                        key={idx}
                        className={`course-item ${
                          changedFields.has(`${arrayPath.join(".")}.${idx}`)
                            ? "changed-field"
                            : ""
                        }`}
                      >
                        <span>{course}</span>
                        <button
                          type="button"
                          className="btn-red"
                          onClick={() => {
                            const newCourses = [...value];
                            newCourses.splice(idx, 1);
                            onDataChange(
                              updateDataAtPath(arrayPath, newCourses)
                            );
                          }}
                        >
                          &times;
                        </button>
                      </div>
                    );
                  })}
                </div>
                <CoursesDropdown
                  value={value}
                  onChange={(newVal) =>
                    onDataChange(updateDataAtPath([...path, key], newVal))
                  }
                  isMandatory={false}
                  useStaticCourses={false}
                  coursesOptions={coursesOptions}
                />
              </div>
            </div>
          );
        }
        if (typeof value === "object" && value !== null) {
          return (
            <div key={key} className="nested-object">
              <h5>{key}</h5>
              <pre>{JSON.stringify(value, null, 2)}</pre>
            </div>
          );
        }
        if (key === "state" && path[0] === "practices") {
          return (
            <StateDropdown
              key={key}
              value={value}
              onChange={(newVal, timezone) => {
                let newData = updateDataAtPath([...path, key], newVal);
                if (timezone) {
                  newData = updateDataAtPath([...path, "timezone"], timezone, newData);
                }
                onDataChange(newData);
              }}
              isMandatory={true}
              path={[...path, key]}
              changedFields={changedFields}
            />
          );
        }
        if (key === "timezone" && path[0] === "practices") {
          return (
            <TimezoneDropdown
              key={key}
              value={value}
              onChange={(newVal) =>
                onDataChange(updateDataAtPath([...path, key], newVal))
              }
              isMandatory={true}
              path={[...path, key]}
              changedFields={changedFields}
            />
          );
        }
        return renderPrimitiveField(key, value, [...path, key]);
      };

      return (
        <div key={index} className="practice-item section-box">
          <div
            className="section-header"
            onClick={() => toggleSection(`practice-${index}`)}
          >
            <h4>{practice.name || `Practice ${index + 1}`}</h4>
            <span className="collapse-icon">
              {collapsedSections[`practice-${index}`] ? "+" : "-"}
            </span>
          </div>
          <div
            className={`section-content ${
              collapsedSections[`practice-${index}`] ? "collapsed" : "expanded"
            }`}
          >
            <div className="practice-details-container">
              <h3>Practice Details</h3>
              {Object.entries(practice).filter(([key]) => key !== "priority").map(([key, value]) => {
                if (key === "coursesMin") {
                  return (
                      <CoursesMinEditor
                        key={key}
                        value={value}
                        onChange={onDataChange}
                        path={[...path, key]}
                        data={data}
                        changedFields={changedFields}
                        coursesOptions={coursesOptions}
                        useStaticCourses={false}
                      />
                  );
                }
                if (key === "workingHours") {
                  // WorkingHoursEditor component using the same pattern as AppointmentSlotsEditor
                  const WorkingHoursEditor = () => {
                    const [newStartHour, setNewStartHour] = React.useState("");
                    const [newStartMinute, setNewStartMinute] = React.useState("");
                    const [newStartPeriod, setNewStartPeriod] = React.useState("AM");
                    const [newEndHour, setNewEndHour] = React.useState("");
                    const [newEndMinute, setNewEndMinute] = React.useState("");
                    const [newEndPeriod, setNewEndPeriod] = React.useState("AM");

                    const hourOptions12 = Array.from({ length: 12 }, (_, i) =>
                      (i + 1).toString()
                    );
                    const minuteOptions = ["00", "15", "30", "45"];
                    const periodOptions = ["AM", "PM"];

                    // Initialize from current value or set defaults for empty
                    React.useEffect(() => {
                      if (typeof value === "string" && value.includes("-")) {
                        const [start, end] = value.split("-").map((s) => s.trim());
                        const [sh, sm] = start.split(":");
                        const [eh, em] = end.split(":");
                        const { hour12: startHour12, period: startPeriod } = convert24To12(sh);
                        const { hour12: endHour12, period: endPeriod } = convert24To12(eh);
                        setNewStartHour(startHour12);
                        setNewStartMinute(sm);
                        setNewStartPeriod(startPeriod);
                        setNewEndHour(endHour12);
                        setNewEndMinute(em);
                        setNewEndPeriod(endPeriod);
                      } else {
                        // Default to 9 AM - 5 PM when working hours is empty
                        setNewStartHour("9");
                        setNewStartMinute("00");
                        setNewStartPeriod("AM");
                        setNewEndHour("5");
                        setNewEndMinute("00");
                        setNewEndPeriod("PM");
                      }
                    }, []); // Remove 'value' from dependency array

                    const handleUpdateWorkingHours = () => {
                      if (
                        newStartHour === "" ||
                        newStartMinute === "" ||
                        newEndHour === "" ||
                        newEndMinute === ""
                      )
                        return;

                      const startHour24 = convert12To24(newStartHour, newStartPeriod);
                      const endHour24 = convert12To24(newEndHour, newEndPeriod);
                      const formattedValue = `${startHour24}:${newStartMinute}-${endHour24}:${newEndMinute}`;
                      onDataChange(updateDataAtPath([...path, key], formattedValue));
                    };

                    const isEditingKey = `practice-${index}-workingHours`;
                    const isEditing = editingStates[isEditingKey]?.isEditing || false;
                    const original = editingStates[isEditingKey]?.original;
                    const setIsEditing = (isEditing, originalValue) => setEditingStates(prev => ({ ...prev, [isEditingKey]: { isEditing, original: originalValue } }));

                    const abbr = getTimezoneAbbr(practice.timezone);
                    const formattedValue =
                      value
                        ? abbr
                          ? `${formatTime12Hour(value.split("-")[0])} <strong>${abbr}</strong> - ${formatTime12Hour(value.split("-")[1])} <strong>${abbr}</strong>`
                          : `${formatTime12Hour(value.split("-")[0])} - ${formatTime12Hour(value.split("-")[1])}`
                        : "Not set";

                    return (
                      <div key={key} className="object-field primitive-field">
                        <label className="field-name">
                          {practiceFieldLabelMap[key] || key}
                          <span className="mandatory-indicator">*</span>:
                        </label>
                        {isEditing ? (
                          <div className="working-hours-edit">
                            <select
                              value={newStartHour}
                              onChange={(e) => setNewStartHour(e.target.value)}
                              className="mr5"
                              aria-label="Select start hour"
                            >
                              <option value="">HH</option>
                              {hourOptions12.map((hour) => (
                                <option key={hour} value={hour}>
                                  {hour}
                                </option>
                              ))}
                            </select>
                            <select
                              value={newStartMinute}
                              onChange={(e) => setNewStartMinute(e.target.value)}
                              className="mr10"
                              aria-label="Select start minute"
                            >
                              <option value="">MM</option>
                              {minuteOptions.map((min) => (
                                <option key={min} value={min}>
                                  {min}
                                </option>
                              ))}
                            </select>
                            <select
                              value={newStartPeriod}
                              onChange={(e) => setNewStartPeriod(e.target.value)}
                              className="mr10"
                              aria-label="Select start period"
                            >
                              {periodOptions.map((period) => (
                                <option key={period} value={period}>
                                  {period}
                                </option>
                              ))}
                            </select>
                            <span className="mr10">to</span>
                            <select
                              value={newEndHour}
                              onChange={(e) => setNewEndHour(e.target.value)}
                              className="mr5"
                              aria-label="Select end hour"
                            >
                              <option value="">HH</option>
                              {hourOptions12.map((hour) => (
                                <option key={hour} value={hour}>
                                  {hour}
                                </option>
                              ))}
                            </select>
                            <select
                              value={newEndMinute}
                              onChange={(e) => setNewEndMinute(e.target.value)}
                              className="mr10"
                              aria-label="Select end minute"
                            >
                              <option value="">MM</option>
                              {minuteOptions.map((min) => (
                                <option key={min} value={min}>
                                  {min}
                                </option>
                              ))}
                            </select>
                            <select
                              value={newEndPeriod}
                              onChange={(e) => setNewEndPeriod(e.target.value)}
                              className="mr10"
                              aria-label="Select end period"
                            >
                              {periodOptions.map((period) => (
                                <option key={period} value={period}>
                                  {period}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={() => {
                                handleUpdateWorkingHours();
                                setIsEditing(false);
                              }}
                              className="btn-green"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                if (original) {
                                  onDataChange(updateDataAtPath([...path, key], original));
                                }
                                setIsEditing(false, null);
                              }}
                              className="btn-red remove-btn"
                              style={{ marginLeft: '8px' }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex-center">
                            <span className="flex-center-WH" dangerouslySetInnerHTML={{ __html: formattedValue }}>
                            </span>
                            <button
                              type="button"
                              onClick={() => setIsEditing(true, value)}
                              className="btn-blue"
                            >
                              Edit
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  };

                  return <WorkingHoursEditor key={key} />;
                }
                return renderField(key, value, [...path, key]);
              })}
              <button
                type="button"
                className="btn-red mt10"
                onClick={() => {
                  const newPractices = [...practices];
                  newPractices.splice(index, 1);
                  onDataChange({ ...data, practices: newPractices });
                }}
              >
                Remove Practice
              </button>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="practices-section">
        {renderSection(
          "Practices",
          <>
            <div>
              {practices.map((practice, index) =>
                renderPracticeItem(practice, index)
              )}
            </div>
            <button
              type="button"
              className="btn-green mt10"
              onClick={() => {
                const newPractices = [
                  ...practices,
                  {
                    name: "",
                    state: "",
                    timezone: "",
                    totalPatients: 0,
                    courses: [],
                    coursesMin: [],
                    //priority: 1,
                    workingHours: "",
                  },
                ];
                onDataChange({ ...data, practices: newPractices });
              }}
            >
              Add Practice
            </button>
          </>,
          "practices"
        )}
      </div>
    );
  };

  const renderProvidersSection = () => {
    const providers = data.providers || [];

    const renderProviderItem = (providerWrapper, index) => {
      const path = ["providers", index, "provider"];
      const provider = providerWrapper.provider || {};

      const renderField = (key, value) => {
        if (key === "practiceRouting") {
          // Practice routing is edited inside AvailabilityEditor only.
          return null;
        }
        if (key === "availability") {
          return (
            <AvailabilityEditor
              key={key}
              availability={value}
              onChange={onDataChange}
              path={[...path, key]}
              data={data}
              changedFields={changedFields}
              setNotification={setNotification}
            />
          );
        }
        if (key === "state") {
          return (
            <StateDropdown
              key={key}
              value={value}
              onChange={(newVal, timezone) => {
                let newData = updateDataAtPath([...path, key], newVal);
                if (timezone) {
                  newData = updateDataAtPath([...path, "timezone"], timezone, newData);
                }
                onDataChange(newData);
              }}
              isMandatory={true}
              path={[...path, key]}
              changedFields={changedFields}
            />
          );
        }
        if (key === "license") {
          return (
            <LicenseDropdown
              key={key}
              value={value}
              onChange={(newVal) =>
                onDataChange(updateDataAtPath([...path, key], newVal))
              }
              isMandatory={true}
              path={[...path, key]}
              changedFields={changedFields}
            />
          );
        }
        if (key === "credentialing") {
          return (
            <CredentialingDropdown
              key={key}
              value={value}
              onChange={(newVal) =>
                onDataChange(updateDataAtPath([...path, key], newVal))
              }
              data={data}
              isMandatory={false}
              path={[...path, key]}
              changedFields={changedFields}
            />
          );
        }
        if (key === "timezone") {
          return (
            <TimezoneDropdown
              key={key}
              value={value}
              onChange={(newVal) =>
                onDataChange(updateDataAtPath([...path, key], newVal))
              }
              isMandatory={true}
              path={[...path, key]}
              changedFields={changedFields}
            />
          );
        }
        if (Array.isArray(value)) {
          return (
            <div key={key} className="array-field">
              <h5>{key}</h5>
              <ul>
                {value.map((item, idx) => (
                  <li key={idx} className="array-item">
                    <input
                      type="text"
                      value={item}
                      onChange={(e) =>
                        onDataChange(
                          updateDataAtPath([...path, key, idx], e.target.value)
                        )
                      }
                    />
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => {
                        const newArray = [...value];
                        newArray.splice(idx, 1);
                        onDataChange(
                          updateDataAtPath([...path, key], newArray)
                        );
                      }}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className="add-btn"
                onClick={() => {
                  const newArray = [...value, ""];
                  onDataChange(updateDataAtPath([...path, key], newArray));
                }}
              >
                Add Item
              </button>
            </div>
          );
        }
        if (typeof value === "object" && value !== null) {
          return (
            <div key={key} className="nested-object">
              <h5>{key}</h5>
              <pre>{JSON.stringify(value, null, 2)}</pre>
            </div>
          );
        }
        return renderPrimitiveField(key, value, [...path, key]);
      };

      return (
        <div key={index} className="provider-item section-box">
          <div
            className="section-header"
            onClick={() => toggleSection(`provider-${index}`)}
          >
            <h4>{provider.name || `Provider ${index + 1}`}</h4>
            <span className="collapse-icon">
              {collapsedSections[`provider-${index}`] ? "+" : "-"}
            </span>
          </div>
          <div
            className={`section-content ${
              collapsedSections[`provider-${index}`] ? "collapsed" : "expanded"
            }`}
          >
            {Object.entries(provider).map(([key, value]) =>
              renderField(key, value)
            )}
            <button
              type="button"
              className="btn-red-1"
              onClick={() => {
                const newProviders = [...providers];
                newProviders.splice(index, 1);
                onDataChange({ ...data, providers: newProviders });
              }}
            >
              Remove Provider
            </button>
          </div>
        </div>
      );
    };

    return (
      <div className="providers-section">
        {renderSection(
          "Providers",
          <>
            <div>
              {providers.map((provider, index) =>
                renderProviderItem(provider, index)
              )}
            </div>
            <button
              type="button"
              className="btn-green mt10"
              onClick={() => {
                const newProviders = [
                  ...providers,
                  {
                    provider: {
                      name: "",
                      state: "",
                      timezone: "",
                      license: [],
                      credentialing: [],
                      availability: {
                        inclusions: { weekly: {} },
                        exclusions: { specificTime: [] },
                      },
                    },
                  },
                ];
                onDataChange({ ...data, providers: newProviders });
              }}
            >
              Add Provider
            </button>
          </>,
          "providers"
        )}
      </div>
    );
  };

  return (
    <div className="json-container">
      <div className="top-level-sections">
        {renderSection("General", renderGeneralSection(), "general")}
        {renderSection(
          "Appointment Slots",
          <AppointmentSlotsEditor
            appointmentSlots={data.appointmentSlots || {}}
            path={["appointmentSlots"]}
            data={data}
            onDataChange={onDataChange}
          />,
          "appointmentSlots"
        )}
        {renderPracticesSection()}
        {renderProvidersSection()}
      </div>
    </div>
  );
}

const CollapsibleHistoryItem = ({ item }) => {
  const [collapsed, setCollapsed] = useState(true);

  return (
    <div className="history-item">
      <div
        className="section-header"
        onClick={() => setCollapsed(!collapsed)}
        style={{ cursor: "pointer", marginBottom: "8px" }}
      >
        <strong>{item.timestamp.toLocaleString()}</strong>
        <span>{collapsed ? "+" : "-"}</span>
      </div>
      {!collapsed && <pre>{JSON.stringify(item.data, null, 2)}</pre>}
    </div>
  );
};

export default JsonRenderer;

export { CollapsibleHistoryItem };
